import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Sparkles } from "lucide-react";
import { LOGO_URL } from "@/lib/constants";

const YEAR_MILESTONES = [
  { year: "2004", label: "The first spike" },
  { year: "2018", label: "First book published" },
  { year: "Today", label: "3D renders & animation" },
];

/**
 * The founder's origin story — verbatim narrative from Uri, dated 2004,
 * explaining how the first Kotzinon was built from a nursing-bottle cap,
 * spikes carved by hand, wood and black paint. A bedtime chapter at a time,
 * the story of the Kotzinons was born.
 */
export default function OriginStorySection() {
  return (
    <section id="how-it-started" className="relative py-20 bg-[hsl(var(--kotz-ink))]">
      <div className="absolute inset-0 bg-grain opacity-20 pointer-events-none" />
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.5 }}
        >
          <Card className="relative overflow-hidden rounded-3xl border border-[hsl(var(--kotz-gold))]/30 bg-[hsl(var(--kotz-ink-2))] p-8 sm:p-12">
            <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-[radial-gradient(circle,hsla(43,92%,56%,0.22),transparent_65%)] pointer-events-none" />

            <Badge
              variant="outline"
              className="bg-[hsl(var(--kotz-gold))]/15 text-[hsl(var(--kotz-gold))] border-[hsl(var(--kotz-gold))]/50 font-mono text-[10px] tracking-[0.25em] uppercase"
            >
              <Sparkles className="h-3 w-3 mr-1" /> Origin · 2004
            </Badge>

            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl tracking-wider text-foreground leading-[1.05]">
              HOW DID THE STORY OF{" "}
              <span className="gold-text">THE KOTZINONS</span> BEGIN?
            </h2>

            <div className="mt-6 space-y-4 text-foreground/75 text-base sm:text-lg leading-relaxed max-w-3xl">
              <p>
                It all started in <span className="text-[hsl(var(--kotz-gold))] font-semibold">2004</span>,
                when Uri wanted to create an original and unique superhero for his son —
                a superhero that didn&apos;t exist on the shelves. A personal superhero.
              </p>
              <p>
                With a helmet made from a{" "}
                <span className="text-foreground font-medium">nursing bottle cap</span>. Spikes from
                a punctured bullet. With a lot of wood carving, sandpaper and black paint, a new
                superhero named <span className="text-[hsl(var(--kotz-gold))] font-semibold">KOTZINONS</span>{" "}
                was born.
              </p>
              <p>
                But what&apos;s a successful character worth without character? For character to
                exist, a character needs a story. <span className="italic">So Uri started writing.</span>
              </p>
              <p>
                Every chapter written during the day, Uri would read to his son in bed at night
                before going to bed. <span className="text-foreground">Thus began the story of the KOTZINONS…</span>
              </p>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <img src={LOGO_URL} alt="Kotzinons logo" className="h-8 w-auto" />
              <p className="text-xs font-mono uppercase tracking-[0.3em] text-[hsl(var(--kotz-gold))]">
                — Uri Menashe Eini, Creator
              </p>
            </div>

            <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
              {YEAR_MILESTONES.map((m) => (
                <div
                  key={m.year}
                  className="p-3 rounded-xl border border-border bg-[hsl(var(--kotz-ink))]/60"
                >
                  <div className="font-display text-xl gold-text leading-none">{m.year}</div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-foreground/55 mt-1">
                    {m.label}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </motion.div>
      </div>
    </section>
  );
}
